"use client";

import * as React from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Home,
  Sprout,
  User,
  Calendar,
  HelpCircle,
  Mail,
  Instagram,
  ChevronRight,
  Leaf,
  Mountain,
  Info,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useMobileNavigation } from "./MobileNavigationContext";

interface MenuItem {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description?: string;
  path?: string;
  external?: boolean;
  href?: string;
  testId: string;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

const menuSections: MenuSection[] = [
  {
    title: "Navigate",
    items: [
      {
        id: "home",
        icon: Home,
        label: "Home",
        description: "Explore our terrariums",
        path: "/",
        testId: "menu-item-home",
      },
      {
        id: "design",
        icon: Sprout,
        label: "Design Your Terrarium",
        description: "Create your living world",
        path: "/checkout",
        testId: "menu-item-design",
      },
      {
        id: "workshop",
        icon: Mountain,
        label: "Nature Experience",
        description: "Join our Colorado workshops",
        path: "/workshop",
        testId: "menu-item-workshop",
      },
    ],
  },
  {
    title: "Your Account",
    items: [
      {
        id: "portal",
        icon: User,
        label: "Customer Portal",
        description: "View your orders",
        path: "/portal",
        testId: "menu-item-portal",
      },
      {
        id: "waitlist",
        icon: Calendar,
        label: "Join Waitlist",
        description: "Be first to know",
        path: "/waitlist",
        testId: "menu-item-waitlist",
      },
    ],
  },
  {
    title: "Connect",
    items: [
      {
        id: "instagram",
        icon: Instagram,
        label: "@lifeitself.studio",
        description: "Follow our journey",
        external: true,
        href: "https://instagram.com/lifeitself.studio",
        testId: "menu-item-instagram",
      },
      {
        id: "email",
        icon: Mail,
        label: "Contact Us",
        description: "hello@lifeitself.studio",
        external: true,
        href: "mailto:hello@lifeitself.studio",
        testId: "menu-item-email",
      },
    ],
  },
];

export function FullScreenMenu() {
  const { isMenuOpen, setIsMenuOpen, currentPath } = useMobileNavigation();
  const [, setLocation] = useLocation();

  const handleItemClick = React.useCallback(
    (item: MenuItem) => {
      if (item.external && item.href) {
        window.open(item.href, "_blank", "noopener,noreferrer");
      } else if (item.path) {
        setLocation(item.path);
      }
      setIsMenuOpen(false);
    },
    [setLocation, setIsMenuOpen]
  );

  const handleClose = React.useCallback(() => {
    setIsMenuOpen(false);
  }, [setIsMenuOpen]);

  // Handle escape key
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isMenuOpen) {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMenuOpen, handleClose]);

  return (
    <AnimatePresence>
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
            aria-hidden="true"
          />

          {/* Full Screen Menu */}
          <motion.div
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "100%" }}
            transition={{
              type: "spring",
              damping: 30,
              stiffness: 300,
            }}
            className={cn(
              "fixed inset-0 z-[70]",
              "bg-background",
              "flex flex-col",
              "overflow-hidden"
            )}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            data-testid="full-screen-menu"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <span className="font-serif text-xl font-semibold">
                  Life Itself Studio
                </span>
              </motion.div>

              <motion.button
                initial={{ opacity: 0, rotate: -90 }}
                animate={{ opacity: 1, rotate: 0 }}
                transition={{ delay: 0.1 }}
                onClick={handleClose}
                className={cn(
                  "p-2 rounded-full",
                  "bg-muted hover:bg-muted/80",
                  "transition-colors duration-150",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                )}
                aria-label="Close menu"
                data-testid="menu-close-button"
              >
                <X className="w-6 h-6" />
              </motion.button>
            </div>

            {/* Scrollable Menu Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              <div className="px-6 py-6 space-y-8">
                {menuSections.map((section, sectionIndex) => (
                  <motion.div
                    key={section.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + sectionIndex * 0.05 }}
                  >
                    <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                      {section.title}
                    </h2>
                    <div className="space-y-1">
                      {section.items.map((item, itemIndex) => {
                        const Icon = item.icon;
                        const isActive = item.path === currentPath;

                        return (
                          <motion.button
                            key={item.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{
                              delay: 0.15 + sectionIndex * 0.05 + itemIndex * 0.03,
                            }}
                            onClick={() => handleItemClick(item)}
                            className={cn(
                              "w-full flex items-center gap-4 p-4 rounded-xl",
                              "transition-all duration-150",
                              "active:scale-[0.98]",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                              isActive
                                ? "bg-primary/10 border border-primary/20"
                                : "hover:bg-muted"
                            )}
                            data-testid={item.testId}
                          >
                            <div
                              className={cn(
                                "w-12 h-12 rounded-xl flex items-center justify-center",
                                isActive
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted"
                              )}
                            >
                              <Icon className="w-6 h-6" />
                            </div>
                            <div className="flex-1 text-left">
                              <div
                                className={cn(
                                  "font-medium",
                                  isActive && "text-primary"
                                )}
                              >
                                {item.label}
                              </div>
                              {item.description && (
                                <div className="text-sm text-muted-foreground">
                                  {item.description}
                                </div>
                              )}
                            </div>
                            <ChevronRight
                              className={cn(
                                "w-5 h-5 text-muted-foreground",
                                isActive && "text-primary"
                              )}
                            />
                          </motion.button>
                        );
                      })}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="px-6 py-4 border-t border-border bg-muted/30"
            >
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Leaf className="w-4 h-4 text-primary" />
                <span>Handcrafted in Colorado</span>
              </div>
            </motion.div>

            {/* Safe area padding for iOS home indicator */}
            <div className="h-safe-area-inset-bottom" />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default FullScreenMenu;
